import { gql } from "@apollo/client";

/**
 * Fetches the authenticated user's role, persona, and permissions from
 * care-management-gql via the `connectCurrentUser` proxy resolver (ACT-1256 D12).
 *
 * The proxy resolver mirrors the connect-gql AcCurrentUser type and is added
 * to care-management-gql's schema on PR #35173. All RBAC decisions in the app
 * derive from this query — the JWT carries no persona/permission claims.
 *
 * NOTE: `connectCurrentUser` requires a valid bearer; a 401 response routes
 * through the shared Apollo errorLink → triggerExpiryWarning (same path as
 * MemberProfilePage auth failures). No separate logout path here.
 */
export const CURRENT_USER = gql`
  query CurrentUser {
    connectCurrentUser {
      id
      email
      displayName
      role {
        code
        displayName
      }
      personas {
        code
        displayName
      }
      currentPersona {
        code
        displayName
        modules {
          moduleId
          sortOrder
          actions
          dataScope {
            requireIpaMatch
            allowedLineOfBusiness
          }
        }
      }
      permissions {
        value
        permissionType
        permissionMeta {
          ipas
          healthPlans
          lineOfBusiness
        }
      }
    }
  }
`;

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface CurrentUserVars {} // query takes no variables

export interface AcDataScope {
  requireIpaMatch: boolean | null;
  allowedLineOfBusiness: string[] | null;
}

export interface AcPersonaModule {
  moduleId: string;
  sortOrder: number;
  actions: string[] | null;
  dataScope: AcDataScope | null;
}

export interface AcPersonaSummary {
  code: string;
  displayName: string;
}

export interface AcCurrentPersona {
  code: string;
  displayName: string;
  modules: AcPersonaModule[];
}

export interface AcRole {
  code: string;
  displayName: string;
}

export interface AcPermission {
  value: string;
  permissionType: string | null;
  permissionMeta: {
    ipas: string[] | null;
    healthPlans: string[] | null;
    lineOfBusiness: string[] | null;
  } | null;
}

export interface CurrentUserResult {
  connectCurrentUser: {
    id: string;
    email: string | null;
    displayName: string | null;
    role: AcRole | null;
    /** All personas assigned to this user. */
    personas: AcPersonaSummary[];
    /** The currently active persona with its server-driven module list. */
    currentPersona: AcCurrentPersona | null;
    /** Flattened permissions — value + permissionType + data-scope meta. */
    permissions: AcPermission[];
  } | null;
}
